import { Document, Page, View, Image, StyleSheet } from '@react-pdf/renderer';
import { getSimpleFontFamily, getFontFamily } from '../../../utils/fontRegistry';
import ChineseText from './ChineseText';
import '../../../utils/simpleChineseHyphenation'; // 确保中文断词系统已加载
import { defaultDocumentData } from '../../../data/defaultPdf';

// 默认图片配置 - 当用户没有上传时使用
const DEFAULT_IMAGES = {
  logo: "https://qiniu-bjjrzy.bjjrzy.com/rsi_practice/qa/textbookContent/titleIcon.jpg",
  background: "https://qiniu-bjjrzy.bjjrzy.com/rsi_practice/qa/textbookContent/bg.png"
};

// 获取图片URL的辅助函数 - 如果用户上传了图片则使用用户图片，否则使用默认图片
const getImageSrc = (userImage, defaultImage) => {
  return userImage && userImage.trim() !== '' ? userImage : defaultImage;
};



// 简化的样式定义 - 使用默认字体系统
const styles = StyleSheet.create({
  body: {
    display: 'flex',
    flexDirection: 'column',
    padding: "0 20px 30px 20px",
    fontFamily: getSimpleFontFamily(), // 使用默认字体
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 5,
  },
  headerLogo: {
    width: 80,
    height: 40
  },
  headerBar: {
    width: "100%",
    height: 35,
    borderBottom: '1px solid #000',
    paddingLeft: 10,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  },
  headerText: {
    fontSize: 14,
    marginTop: 5,
    fontFamily: getSimpleFontFamily(), // 使用默认字体
  },
  pageNumberText:{
    fontSize: 10,
    marginTop: 5,
    fontFamily: getSimpleFontFamily(), // 使用默认字体
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
  },
  contentLeft: {
    width: '60%',
    padding: "5px 5px 5px 0px",
    marginRight:5,
  },
  contentImg:{
    width:1456,
    height:816
  },
  contentDivider: {
    width: 1,
    backgroundColor: '#890d28',
  },
  contentRight: {
    flex: 1,
  },
  mainTitle: {
    marginTop: 10,
    alignItems: "center",
    fontSize: 24,
    textAlign:"center",
    fontFamily: getSimpleFontFamily(), // 使用默认字体
  },
  subTitle: {
    marginTop: 10,
    alignItems: "center",
    fontSize: 20,
    textAlign:"center",
    fontFamily: getSimpleFontFamily(), // 使用默认字体
  },
  paragraph: {
    marginTop: 5,
    alignItems: "flex-start",
    fontSize: 12,
    lineHeight:1.5,
    marginLeft:10,
    fontFamily: getSimpleFontFamily(), // 使用默认字体
  },
  summary: {
    backgroundColor: "#e6eef6",
    padding: 5,
    fontSize: 10,
    fontFamily: getSimpleFontFamily(), // 使用默认字体
  },
  chineseGuide: {
    marginTop: 10,
    backgroundColor: "#bfd4e6",
    padding: 5,
    fontSize: 10,
    fontFamily: getSimpleFontFamily(), // 使用默认字体
  },
  chineseGuideTitle: {
    fontSize: 16,
    fontFamily: getSimpleFontFamily(), // 使用默认字体
  },
  wordItem: {
    marginTop: 5,
    alignItems: "flex-start",
    fontSize: 14,
    padding: 5,
    width: "100%",
    backgroundColor: "#e6eef6",
    lineHeight: 1,
    fontFamily: getSimpleFontFamily(), // 使用默认字体
  },
  wordIndex: {
    alignItems: "center",
    fontFamily: getSimpleFontFamily(), // 使用默认字体
    fontSize: 10,
    width: "100%",
  },
  wordName: {
    fontWeight: "bold",
    color: "#34488f",
    fontSize: 10,
    fontFamily: getSimpleFontFamily(), // 使用默认字体
  },
  phonetic: {
    fontSize: 10,
    fontFamily: getFontFamily('LXGWWenKaiTC'), // 使用 LXGWWenKaiTC 字体显示音标
  },
  phoneticContainer: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  underlinedWord: {
    textDecoration: 'underline',
    // textDecorationColor: 'red',
    textDecorationColor: '#890d28',
    textDecorationStyle: 'solid',
    display: 'flex',
    color: '#000',
    paddingBottom: 6, // 在文字底部添加内边距，增加与下划线的距离
    // lineHeight: 1.5, // 增加行高，为下划线提供更多空间
    textUnderlineOffset: 3, // 如果React PDF支持，设置下划线偏移距离
    textDecorationThickness: 1, // 设置下划线粗细
  },
  // 水印样式 - React PDF层级控制
  watermarkContainerFront: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    // 使用fixed和绝对定位确保覆盖
  },
  watermarkContainerBack: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    // 背景层水印
  },
  watermarkText: {
    position: 'absolute',
    fontSize: 24,
    fontFamily: getSimpleFontFamily(),
    color: '#cccccc',
    opacity: 0.3,
    transform: 'rotate(-45deg)',
    transformOrigin: 'center',
    // React PDF中移除zIndex，依赖渲染顺序
  },
});

// 水印组件 - 支持多文本平铺、旋转、透明度和层级配置，支持排除图片区域
const Watermark = ({
  text = '水印文本',
  opacity = 0.3,
  rotation = -45,
  fontSize = 12,
  color = '#cccccc',
  density = 'medium', // low, medium, high
  layer = 'front', // front, back
  excludeImages = true // 是否排除图片区域
}) => {
  if (!text || text.trim() === '') return null;

  // 根据密度设置间距
  const spacingMap = {
    low: { x: 300, y: 200 },
    medium: { x: 200, y: 150 },
    high: { x: 150, y: 100 }
  };

  const spacing = spacingMap[density] || spacingMap.medium;

  // 定义需要排除的图片区域（基于A4页面坐标）
  const imageExcludeAreas = excludeImages ? [
    // Header Logo 区域
    { x: 0, y: 0, width: 100, height: 50 },
    // Background Image 区域 (左侧内容区域的背景图)
    { x: 0, y: 50, width: 350, height: 400 }
  ] : [];

  // 检查水印位置是否与排除区域重叠
  const isInExcludeArea = (x, y, watermarkWidth = 80, watermarkHeight = 20) => {
    return imageExcludeAreas.some(area => {
      return x < area.x + area.width &&
             x + watermarkWidth > area.x &&
             y < area.y + area.height &&
             y + watermarkHeight > area.y;
    });
  };

  // 计算水印位置 - 创建网格布局
  const watermarks = [];
  const pageWidth = 595; // A4页面宽度（点）
  const pageHeight = 842; // A4页面高度（点）

  // 计算行列数
  const cols = Math.ceil(pageWidth / spacing.x) + 1;
  const rows = Math.ceil(pageHeight / spacing.y) + 1;

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = col * spacing.x - 50; // 稍微偏移避免边缘
      const y = row * spacing.y + 50;

      // 如果启用了图片排除功能，检查是否在排除区域内
      if (excludeImages && isInExcludeArea(x, y)) {
        continue; // 跳过在排除区域内的水印
      }

      watermarks.push(
        <ChineseText
          key={`watermark-${row}-${col}`}
          style={{
            ...styles.watermarkText,
            left: x,
            top: y,
            fontSize: fontSize,
            color: layer === 'front' ? color : color, // 保持原色
            opacity: layer === 'front' ? Math.max(opacity, 0.6) : opacity, // 最前层提高透明度便于观察
            transform: `rotate(${rotation}deg)`,
          }}
        >
          {text}
        </ChineseText>
      );
    }
  }

  // 根据层级选择不同的容器样式和fixed属性
  const containerStyle = layer === 'front'
    ? styles.watermarkContainerFront
    : styles.watermarkContainerBack;

  // 最前层使用fixed确保覆盖所有内容，最后层不使用fixed
  return (
    <View style={containerStyle} fixed={layer === 'front'}>
      {watermarks}
    </View>
  );
};

// 页头组件 - 支持动态数据，带默认图片占位
const Header = ({ currentDocumentData }) => (
  <View style={styles.header} fixed>
    <Image
      style={styles.headerLogo}
      src={getImageSrc(currentDocumentData.header.logoUrl, DEFAULT_IMAGES.logo)}
    />
    <View style={styles.headerBar}>
      <ChineseText style={styles.headerText}>
        {currentDocumentData.header.text}
      </ChineseText>
       <ChineseText style={styles.pageNumberText}
          render={({ pageNumber, totalPages }) =>
            totalPages ? `${pageNumber} / ${totalPages}` : ''
          }
        />
    </View>
  </View>
);

// 单词总结组件
const SummarizeWordsCom = ({ words = [], useNumbering = false, title = "", style = {} }) => {
  return <View style={{ backgroundColor: "#e6ecf4", border: "1px solid #03468f", padding: "10px", marginTop: 10, ...style }}>
    <ChineseText style={{ textAlign: "center", marginBottom: "10px", fontSize: 14 }}>{title}</ChineseText>
    <View>
      {words.map((word, index) => (
        <ChineseText style={{ fontSize: 12, lineHeight: 1.7 }} key={index}>
          {useNumbering ? `[${index + 1}] ` : '• '}{word.content}
        </ChineseText>
      ))}
    </View>
  </View>
}

// 处理英文段落，为单词添加红色下划线 - 支持高亮形式字段和自定义划线单词
const renderEnglishWithUnderline = (text, wordsList, underlinedWords = []) => {
  if (!text) return text;

  // 创建需要标记的单词集合
  const wordsToMark = new Set();

  // 处理重点单词列表（来自words数组）
  if (wordsList && wordsList.length > 0) {
    wordsList.forEach(word => {
      // 如果有高亮形式字段且不为空，使用高亮形式；否则使用原形
      const highlightForm = word.highlightForm && word.highlightForm.trim() !== ''
        ? word.highlightForm.trim()
        : word.name;

      // 支持多个高亮形式（用逗号分隔）
      const forms = highlightForm.split(',').map(form => form.trim().toLowerCase());
      forms.forEach(form => {
        if (form) {
          wordsToMark.add(form);
        }
      });
    });
  }

  // 处理自定义划线单词列表（来自underlinedWords数组）
  if (underlinedWords && underlinedWords.length > 0) {
    underlinedWords.forEach(word => {
      if (word && word.trim()) {
        // 支持多个单词形式（用逗号分隔）
        const forms = word.split(',').map(form => form.trim().toLowerCase());
        forms.forEach(form => {
          if (form) {
            wordsToMark.add(form);
          }
        });
      }
    });
  }

  // 如果没有需要标记的单词，直接返回原文本
  if (wordsToMark.size === 0) return text;

  // 将文本分割成单词，保留标点符号
  const parts = [];
  let currentIndex = 0;

  // 正则表达式匹配单词（包括带撇号的单词如don't）
  const wordRegex = /[a-zA-Z]+(?:'[a-zA-Z]+)?/g;
  let match;

  while ((match = wordRegex.exec(text)) !== null) {
    // 添加匹配前的文本
    if (match.index > currentIndex) {
      parts.push(text.substring(currentIndex, match.index));
    }

    const word = match[0];
    const wordLower = word.toLowerCase();

    // 检查单词是否需要标记（不区分大小写）
    if (wordsToMark.has(wordLower)) {
      parts.push(
        <ChineseText key={match.index} style={styles.underlinedWord}>
          {word}
        </ChineseText>
      );
    } else {
      parts.push(word);
    }

    currentIndex = match.index + word.length;
  }

  // 添加剩余文本
  if (currentIndex < text.length) {
    parts.push(text.substring(currentIndex));
  }

  return parts;
};


const renderPhoneticSimple = (phoneticText) => {
  if (!phoneticText) return phoneticText;

  // 简单的正则：匹配 /音标/ 格式
  const parts = phoneticText.split(/(\/[^/]+\/)/g);

  return parts.map((part, index) => {
    if (part.startsWith('/') && part.endsWith('/')) {
      // 音标部分使用 LXGWWenKaiTC 字体，防止换行
      return (
        <ChineseText
          key={index}
          style={{
            fontSize: 10,
            fontFamily: getFontFamily('NotoSerif'),
            whiteSpace: 'nowrap' // 防止换行
          }}
        >
          {part}
        </ChineseText>
      );
    } else {
      // 中文部分使用默认字体，防止换行
      return (
        <ChineseText
          key={index}
          style={{
            fontSize: 10,
            fontFamily: getSimpleFontFamily(),
            whiteSpace: 'nowrap' // 防止换行
          }}
        >
          {part}
        </ChineseText>
      );
    }
  });
};


// 处理例句，为目标单词添加高亮显示（支持智能高亮）
const renderExampleWithHighlight = (exampleText, targetWord, highlightWord = null) => {
  if (!exampleText || !targetWord) return exampleText;

  // 确定要高亮的单词：优先使用 highlightWord，否则使用 targetWord
  const wordToHighlight = highlightWord && highlightWord.trim() !== '' ? highlightWord : targetWord;

  // 将文本分割成单词，保留标点符号
  const parts = [];
  let currentIndex = 0;

  // 正则表达式匹配单词（包括带撇号的单词如don't）
  const wordRegex = /[a-zA-Z]+(?:'[a-zA-Z]+)?/g;
  let match;

  while ((match = wordRegex.exec(exampleText)) !== null) {
    // 添加匹配前的文本
    if (match.index > currentIndex) {
      parts.push(exampleText.substring(currentIndex, match.index));
    }

    const word = match[0];
    // 检查单词是否是目标单词（不区分大小写）
    if (word.toLowerCase() === wordToHighlight.toLowerCase()) {
      parts.push(
        <ChineseText key={match.index} style={{ fontSize: 10, color: '#34488f', fontWeight: 'bold' }}>
          {word}
        </ChineseText>
      );
    } else {
      parts.push(word);
    }

    currentIndex = match.index + word.length;
  }

  // 添加剩余文本
  if (currentIndex < exampleText.length) {
    parts.push(exampleText.substring(currentIndex));
  }

  return parts;
};

// 主文档组件 - 支持动态数据配置和水印功能
// 接收documentData作为props，如果没有传入则使用默认模板数据
const PDFDocument = ({
  documentData = null,
  watermarkConfig = null, // 水印配置
  wordIndexMargins = {},
}) => {
  // 使用传入的数据或默认数据
  const currentDocumentData = documentData || defaultDocumentData;

  return (
    <Document>
      <Page style={styles.body}>
        {/* 最后层水印 - 在所有内容之前渲染 */}
        {watermarkConfig && watermarkConfig.enabled && watermarkConfig.layer === 'back' && (
          <Watermark
            text={watermarkConfig.text}
            opacity={watermarkConfig.opacity}
            rotation={watermarkConfig.rotation}
            fontSize={watermarkConfig.fontSize}
            color={watermarkConfig.color}
            density={watermarkConfig.density}
            layer={watermarkConfig.layer}
            excludeImages={watermarkConfig.excludeImages !== false} // 默认排除图片区域
          />
        )}

        <Header currentDocumentData={currentDocumentData} />

        <View style={styles.content}>
          <View style={styles.contentLeft}>
            <Image  src={getImageSrc(currentDocumentData.backgroundImage, DEFAULT_IMAGES.background)} />
            {/* 题目 */}
            <View style={styles.mainTitle}>
              <ChineseText>
                {currentDocumentData.title?.english}
              </ChineseText>
            </View>
            <View style={styles.subTitle}>
              <ChineseText>
                {currentDocumentData.title?.chinese}
              </ChineseText>
            </View>
          {/* 段落 */}
          {
            currentDocumentData.paragraphs?.map((item, index) => (
              <View key={index} style={styles.paragraph}>
                <ChineseText style={{lineHeight:1.7,fontSize:13}}>{`【${index + 1}】`}{renderEnglishWithUnderline(item?.english, item.words, item.underlinedWords)}</ChineseText>
                <ChineseText style={{lineHeight:1.7,fontSize:12}}>{item?.chinese}</ChineseText>
              </View>
            ))
          }
          <SummarizeWordsCom words={currentDocumentData.phraseCollection.items} useNumbering={false} title={currentDocumentData.phraseCollection.title} style={{ marginRight: 10 }} />
        </View>
        <View style={styles.contentDivider}></View>
        <View style={styles.contentRight}>
          {/* 缩略简介 */}
          <View style={styles.summary}>
            <View>
              <ChineseText>{currentDocumentData.summary.title}</ChineseText>
            </View>
            <View>
              <ChineseText>{currentDocumentData.summary.source}</ChineseText>
            </View>
          </View>
          {/* 中文导读 */}
          <View style={styles.chineseGuide}>
            <View style={styles.chineseGuideTitle}>
              <ChineseText>{currentDocumentData.chineseGuide.title}</ChineseText>
            </View>
            {currentDocumentData.chineseGuide.content.map((line, index) => (
              <View key={index}>
                <ChineseText style={{fontSize:11}}>{line}</ChineseText>
              </View>
            ))}
          </View>
          {/* 单词区域 - 防止分页分割 */}
          {
            currentDocumentData.paragraphs.map((item, index) => (
              <View
                key={index}
                style={styles.wordSection}
                // wrap={false}  // 防止这个单词组被分页分割
                break={false} // 确保内容不会在页面边界被切断
              >
                  <View style={[
                    styles.wordIndex,
                    {
                      marginTop: (wordIndexMargins && wordIndexMargins[index] !== undefined) ? wordIndexMargins[index] : 8,
                      marginBottom: (wordIndexMargins && wordIndexMargins[index] !== undefined) ? wordIndexMargins[index] : 8
                    }
                  ]}>
                  <ChineseText>{`【${index + 1}】`}</ChineseText>
                </View>
                {item.words && item.words.map((word, i) => (
                  <View key={`${index}-${i}`} style={styles.wordItem}>
                    <View><ChineseText style={styles.wordName}>{word.name}</ChineseText></View>
                    <View style={styles.phoneticContainer}>
                      <ChineseText>{renderPhoneticSimple(word.phonetic)}</ChineseText>
                      {/* 备用音标 - 如果存在则在主音标下方显示 */}
                      {word.alternativePhonetic && (
                        <View style={{ marginTop: 2 }}>
                          <ChineseText >
                            {renderPhoneticSimple(word.alternativePhonetic)}
                          </ChineseText>
                        </View>
                      )}
                    </View>
                    <View><ChineseText style={{ fontSize: 10 }}>{word.partOfSpeech}</ChineseText></View>
                    <View><ChineseText style={{ fontSize: 10 }}>{word.scopeWord}</ChineseText></View>
                    <View><ChineseText style={{ fontSize: 10 }}>[例]{renderExampleWithHighlight(word.example, word.name, word.highlightWord)}</ChineseText></View>
                    <View><ChineseText style={{ fontSize: 10 }}>{word.chExample}</ChineseText></View>
                  </View>
                ))}
              </View>
            ))
          }
          <SummarizeWordsCom words={currentDocumentData.paragraphSummary.items} useNumbering={true} title={currentDocumentData.paragraphSummary.title} style={{ marginLeft: 10 }} />
        </View>
      </View>

        {/* 最前层水印 - 在所有内容之后渲染，使用fixed确保覆盖所有元素 */}
        {watermarkConfig && watermarkConfig.enabled && watermarkConfig.layer === 'front' && (
          <View style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%'
          }} fixed>
            <Watermark
              text={watermarkConfig.text}
              opacity={watermarkConfig.opacity}
              rotation={watermarkConfig.rotation}
              fontSize={watermarkConfig.fontSize}
              color={watermarkConfig.color}
              density={watermarkConfig.density}
              layer={watermarkConfig.layer}
              excludeImages={watermarkConfig.excludeImages !== false} // 默认排除图片区域
            />
          </View>
        )}
      </Page>
    </Document>
  );
};

export default PDFDocument;
